/**
 * Multi-Slide Builder Agent
 * 
 * Top-level orchestrator for building presentations section-by-section.
 * Coordinates research, Excel creation, and section execution.
 * 
 * ## Architecture
 * 
 * This agent:
 * 1. Iterates through sections in the plan
 * 2. For each section:
 *    a. Searches for reference slides in style guide
 *    b. Gathers research data (AlphaSense, external files)
 *    c. Creates Excel data if needed (charts, tables)
 *    d. Calls ExecuteSectionTool to build the section
 * 3. Tracks progress and handles pause/resume
 * 
 * ## Key Features
 * 
 * - **Section-by-section**: Processes plan in logical chunks
 * - **Research orchestration**: Calls existing search agents
 * - **Excel integration**: Uses LinkedExcelEditor for data
 * - **Progress tracking**: Emits events for UI updates
 * - **1M token context**: Extended context for large plans
 * - **No streaming**: Static LLM calls for reliability
 * 
 * ## Usage
 * 
 * ```typescript
 * const builder = new MultiSlideBuilderAgent();
 * const result = await builder.execute({
 *     planId: "plan_123",
 *     plan: presentationPlan,
 *     styleGuidedocumentId: "deck_abc",
 *     workingPresentationPath: "/path/to/working.pptx",
 *     workingWorkbookPath: "/path/to/working.xlsx",
 * }, callbacks);
 * ```
 */

import { z } from "zod";
import { AgentBaseClass } from "../base/agentBaseClass";
import { ToolDescription } from "../base/toolDescription";
import { ToolBase } from "../base/toolBase";
import type { SubmitResultsParams } from "../base/submitResults.schema";
import { ChatMessage } from "../../globals/llm/baseAdapter";
import {
    AgentResult,
    ResultItem,
    ResolvedContextItem,
    ThreadId,
    CancellationToken,
    AgentCallbacks,
} from "../../core/types";

import {
    MultiSlideBuilderParamsSchema,
    type MultiSlideBuilderParams,
    type MultiSlideBuilderResponse,
    type SectionBuildResult,
    type SectionDefinition,
} from "./multiSlideBuilder.schema";

// Tools
import { ExecuteSectionTool } from "./tools/executeSection";
import { BuilderReadSlideTool } from "./tools/builderReadSlide";

// Research/Search agents (used as tools)
import { AlphaSenseAgent, ExternalFileSearchAgent, PresentationLibrarySearchAgent, ImageSearchAgent } from "../search";
import { LinkedExcelEditorAgent } from "../linkedExcelEditor";

// Shared prompt content
import {
    DENSITY_SECTION_FULL,
    STYLE_GUIDE_PHILOSOPHY,
    SEARCH_AGENT_GUIDANCE,
    AREA_DEFINITION_BEST_PRACTICES,
    generateAvailableSlidesSection,
} from "./prompts";

// ═══════════════════════════════════════════════════════════════════════════
// MULTI-SLIDE BUILDER AGENT
// ═══════════════════════════════════════════════════════════════════════════

export class MultiSlideBuilderAgent extends AgentBaseClass<MultiSlideBuilderParams, AgentResult<MultiSlideBuilderResponse>> {
    
    // ═══════════════════════════════════════════════════════════════════════
    // REQUIRED IMPLEMENTATIONS
    // ═══════════════════════════════════════════════════════════════════════
    
    readonly agentType = 'multiSlideBuilder';
    readonly name = 'multiSlideBuilderAgent';
    readonly isAgent = true;
    
    // Schema for validation
    public paramsSchema = MultiSlideBuilderParamsSchema as unknown as z.ZodType<MultiSlideBuilderParams>;
    
    // ═══════════════════════════════════════════════════════════════════════
    // INFRASTRUCTURE FLAGS
    // ═══════════════════════════════════════════════════════════════════════
    
    protected useThreadManagement = true;   // Required for useSubmitResultsPattern
    protected useTaskTracking = true;       // For progress tracking
    protected useContextResolution = false; // Context passed directly
    protected useSubmitResultsPattern = true;  // Use submitResults tool
    protected stream = false;               // No streaming - static calls
    // maxResponseTokens: inherits 0 from base → adapter uses model maximum
    protected maxToolLoopIterations = 500;  // Many iterations for full builds
    
    // Enable 1M token context
    protected extendedContext = true;
    
    // ═══════════════════════════════════════════════════════════════════════
    // SESSION STATE
    // ═══════════════════════════════════════════════════════════════════════
    
    private buildParams: MultiSlideBuilderParams | null = null;
    private sectionResults: SectionBuildResult[] = [];
    private currentSectionIndex: number = 0;
    private totalSlidesBuilt: number = 0;
    
    // ═══════════════════════════════════════════════════════════════════════
    // CONSTRUCTOR
    // ═══════════════════════════════════════════════════════════════════════
    
    constructor() {
        super();
        
        // Tools will be initialized in onBeforeExecute with plan ID
        this.toolCalls = [];
        
        this.description = new ToolDescription(
            "multiSlideBuilderAgent",
            `Orchestrate building a complete presentation from a plan.
            
Processes sections one at a time:
1. Search for reference slides in style guide
2. Gather research data if needed
3. Create Excel data if needed (charts, tables)
4. Build the section using ExecutionAgent

Tracks progress and supports pause/resume.`,
            MultiSlideBuilderParamsSchema
        );
    }
    
    // ═══════════════════════════════════════════════════════════════════════
    // LIFECYCLE HOOKS
    // ═══════════════════════════════════════════════════════════════════════
    
    /**
     * Called before execute - initialize tools and state
     * Must match base class signature: beforeExecute(context)
     */
    protected async beforeExecute(context: {
        params: MultiSlideBuilderParams;
        threadId: ThreadId;
        taskId: string;
        cancellationToken: CancellationToken;
        resolvedContext: ResolvedContextItem[];
        conversationId: string;
        documentId: string;
        callbacks?: AgentCallbacks;
    }): Promise<void> {
        const params = context.params;
        this.buildParams = params;
        this.sectionResults = [];
        this.currentSectionIndex = 0;
        this.totalSlidesBuilt = 0;
        
        console.log(`[MultiSlideBuilderAgent] Starting build for plan: ${params.planId}`);
        console.log(`[MultiSlideBuilderAgent] Sections: ${params.plan.sections.length}`);
        console.log(`[MultiSlideBuilderAgent] Total slides: ${params.plan.slideCount}`);
        console.log(`[MultiSlideBuilderAgent] Slide dimensions: ${params.slideDimensions.width} x ${params.slideDimensions.height} points`);
        
        // Initialize tools with plan context
        this.toolCalls = [
            // Section execution (wraps ExecutionAgent) - pass slideDimensions and styleGuideSlideCount
            new ExecuteSectionTool(params.planId, params.slideDimensions, params.styleGuideSlideCount),
            
            // Direct tools - BuilderReadSlideTool needs styleGuideSlideCount for index resolution
            new BuilderReadSlideTool(params.planId, params.styleGuideSlideCount),
            
            // Research agents (agent-as-tool pattern)
            new AlphaSenseAgent(),
            new ExternalFileSearchAgent(),
            new PresentationLibrarySearchAgent(),
            new ImageSearchAgent(),
            
            // Excel editing
            new LinkedExcelEditorAgent({ submitToolkitMode: "override" }),
            
            // submitResults is auto-injected by useSubmitResultsPattern
        ];
        
        // Configure sub-agents for non-streaming mode and propagate registry
        // Non-streaming gives us complete responses instead of fragmented chunks
        for (const tool of this.toolCalls) {
            if (tool instanceof AgentBaseClass) {
                // Disable streaming so we get complete responses (better for Activity Log)
                tool.setStreamMode(false);
                console.log(`[${this.name}] Disabled streaming for sub-agent: ${tool.name}`);
                
                // Propagate parent registry
                if (this.getParentRegistry()) {
                    tool.setParentRegistry(this.getParentRegistry()!);
                    console.log(`[${this.name}] Propagated registry to sub-agent: ${tool.name}`);
                }
            }
        }
    }
    
    /**
     * Generate system prompt for the builder
     * 
     * Uses the new standardized prompt structure.
     */
    public generateSystemPrompt(_resolvedContext?: ResolvedContextItem[]): string {
        const params = this.buildParams;
        if (!params) {
            return "You are a presentation builder. Build slides section by section.";
        }
        
        // Import the builder function
        const { buildMultiSlideBuilderSystemPrompt } = require('./multiSlideBuilder/prompt');
        
        return buildMultiSlideBuilderSystemPrompt({
            planTitle: params.plan.title,
            slideCount: params.plan.slideCount,
            sectionCount: params.plan.sections.length,
            sections: params.plan.sections,
            planId: params.planId,
            styleGuidedocumentId: params.styleGuidedocumentId,
            styleGuideSlideCount: params.styleGuideSlideCount,
            workingPresentationPath: params.workingPresentationPath,
            workingWorkbookPath: params.workingWorkbookPath,
            slideDimensions: params.slideDimensions,
            styleGuideOverview: params.styleGuideOverview,
            tools: this.toolCalls,
        });
    }
    
    // buildStyleGuideOverviewSection and buildFullPlanText moved to prompt/index.ts
    
    /**
     * Build initial messages for the tool loop (synchronous)
     */
    protected buildInitialMessages(params: MultiSlideBuilderParams, _resolvedContext?: ResolvedContextItem[]): ChatMessage[] {
        const firstSection = params.plan.sections[0];
        const planTitle = params.plan.title || 'Presentation';
        
        return [
            {
                role: "user" as const,
                content: `Build the presentation "${planTitle}".

Total sections: ${params.plan.sections.length}
Total slides: ${params.plan.slideCount}

Start with section 1: "${firstSection?.name || 'First section'}"
- Search for reference slides using presentationLibrarySearchAgent
- Gather any needed research
- Create Excel data if needed
- Then call executeSection

Process all sections in order, then call submitResults when complete.`,
            },
        ];
    }
    
    /**
     * Track section results from tool calls
     */
    protected onToolResult(toolName: string, result: unknown): void {
        if (toolName === 'executeSection' && result && typeof result === 'object') {
            const sectionResult = result as Record<string, unknown>;
            
            this.sectionResults.push({
                sectionId: sectionResult.sectionId as string ?? 'unknown',
                sectionTitle: sectionResult.sectionTitle as string ?? 'unknown',
                slidesBuilt: sectionResult.slidesBuilt as number ?? 0,
                success: sectionResult.success as boolean ?? false,
                errors: sectionResult.errors as string[] ?? [],
                checkpointId: sectionResult.checkpointAfter as string,
            });
            
            this.currentSectionIndex++;
            this.totalSlidesBuilt += (sectionResult.slidesBuilt as number) ?? 0;
            
            console.log(`[MultiSlideBuilderAgent] Section ${this.currentSectionIndex}/${this.buildParams?.plan.sections.length} complete`);
        }
    }
    
    /**
     * Process results from the submitResults tool.
     * Required implementation for AgentBaseClass with useSubmitResultsPattern = true.
     */
    protected override async processSubmittedResults(
        submittedResults: SubmitResultsParams,
        _params: MultiSlideBuilderParams,
        _context: {
            params: MultiSlideBuilderParams;
            threadId: ThreadId;
            taskId: string;
            cancellationToken: CancellationToken;
            resolvedContext: ResolvedContextItem[];
            conversationId: string;
            documentId: string;
            callbacks?: AgentCallbacks;
        }
    ): Promise<ResultItem<MultiSlideBuilderResponse>[]> {
        const params = this.buildParams!;
        
        // Count successful and skipped sections
        const successfulSections = this.sectionResults.filter(r => r.success);
        const skippedSections = this.sectionResults.filter(r => r.skipped);
        
        const response: MultiSlideBuilderResponse = {
            // Success if we built at least something (allow partial success)
            success: successfulSections.length > 0,
            planId: params.planId,
            sectionsBuilt: successfulSections.length,
            sectionsSkipped: skippedSections.length,
            totalSections: params.plan.sections.length,
            slidesBuilt: this.totalSlidesBuilt,
            totalSlides: params.plan.slideCount,
            sectionResults: this.sectionResults,
            errors: this.sectionResults.flatMap(r => r.errors || []),
            completedAt: new Date().toISOString(),
        };
        
        // Build summary
        let summary = submittedResults.summary;
        if (!summary) {
            summary = `Built ${response.sectionsBuilt}/${response.totalSections} sections, ${response.slidesBuilt}/${response.totalSlides} slides`;
            if (skippedSections.length > 0) {
                summary += ` (${skippedSections.length} skipped)`;
            }
        }
        
        // Return as a single result item
        return [{
            requirementId: 'build_presentation',
            relevantResults: response,
            rationale: summary,
        }];
    }
    
    /**
     * Build final result from processed results
     */
    protected override buildFinalResult(
        results: ResultItem<unknown>[],
        threadId: ThreadId,
        tokensUsed?: number
    ): AgentResult<MultiSlideBuilderResponse> {
        if (results.length === 0) {
            return this.buildPartialResult(results as ResultItem<MultiSlideBuilderResponse>[], threadId, tokensUsed);
        }
        return this.buildResult(results as ResultItem<MultiSlideBuilderResponse>[], threadId, tokensUsed);
    }
    
}

export default MultiSlideBuilderAgent;


